import { create } from 'zustand';
import {
  createSystemPrompt,
  deleteSystemPrompt,
  getActiveAccountId,
  listSystemPrompts,
  updateSystemPrompt,
  type SystemPrompt,
  type SystemPromptInput,
} from '@auw/api-client';
import { activeTenantKey } from '@auw/tenant';

export const SYSTEM_PROMPT_MODES = ['agent', 'doc-agent', 'gtc-agent', 'purchase-agent'] as const;
export type SystemPromptMode = typeof SYSTEM_PROMPT_MODES[number];
type SelectedPromptIds = Record<SystemPromptMode, string | null>;

export function isSystemPromptMode(value: string): value is SystemPromptMode {
  return (SYSTEM_PROMPT_MODES as readonly string[]).includes(value);
}

const emptySelection = (): SelectedPromptIds => ({
  agent: null,
  'doc-agent': null,
  'gtc-agent': null,
  'purchase-agent': null,
});

function storageKey(accountId: string): string {
  return `${activeTenantKey('system_prompt_selection')}:${accountId}`;
}

function readSelection(accountId: string | null): SelectedPromptIds {
  const selected = emptySelection();
  if (!accountId) return selected;
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(storageKey(accountId)) || '{}');
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
      for (const mode of SYSTEM_PROMPT_MODES) {
        const id = (stored as Record<string, unknown>)[mode];
        if (typeof id === 'string' && id.trim()) selected[mode] = id;
      }
    }
  } catch {
    // Browser storage is optional; prompt content is stored on the server only.
  }
  return selected;
}

function persistSelection(accountId: string | null, selected: SelectedPromptIds): void {
  if (!accountId) return;
  try {
    localStorage.setItem(storageKey(accountId), JSON.stringify(selected));
  } catch {
    // Selection still works for this session when storage is unavailable.
  }
}

export function clearStoredSystemPromptSelection(accountId: string | null): void {
  if (!accountId) return;
  try {
    localStorage.removeItem(storageKey(accountId));
  } catch {
    // Account cleanup must also work when storage is unavailable.
  }
}

interface SystemPromptState {
  accountId: string | null;
  prompts: SystemPrompt[];
  selectedPromptIds: SelectedPromptIds;
  isLoading: boolean;
  isSaving: boolean;
  hasLoaded: boolean;
  error: string | null;
  fetchPrompts: () => Promise<void>;
  selectPrompt: (mode: SystemPromptMode, id: string | null) => void;
  savePrompt: (input: SystemPromptInput, id?: string) => Promise<SystemPrompt | null>;
  deletePrompt: (id: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

let sessionEpoch = 0;

function initialState(accountId: string | null) {
  return {
    accountId,
    prompts: [] as SystemPrompt[],
    selectedPromptIds: readSelection(accountId),
    isLoading: false,
    isSaving: false,
    hasLoaded: false,
    error: null as string | null,
  };
}

export const useSystemPromptStore = create<SystemPromptState>((set, get) => {
  const syncAccount = () => {
    const accountId = getActiveAccountId();
    if (get().accountId !== accountId) {
      sessionEpoch += 1;
      set(initialState(accountId));
    }
    return accountId;
  };
  const isCurrent = (accountId: string, epoch: number) =>
    getActiveAccountId() === accountId && get().accountId === accountId && sessionEpoch === epoch;
  const errorMessage = (error: unknown) =>
    error instanceof Error ? error.message : '시스템 프롬프트를 처리하지 못했습니다. 다시 시도해 주세요.';

  return {
    // Auth and tenant bootstrap may not have completed at module evaluation.
    ...initialState(null),

    fetchPrompts: async () => {
      const accountId = syncAccount();
      if (!accountId || get().isLoading || get().isSaving) return;
      const epoch = sessionEpoch;
      set({ isLoading: true, error: null });
      try {
        const { prompts } = await listSystemPrompts();
        if (!isCurrent(accountId, epoch)) return;
        const selectedPromptIds = { ...get().selectedPromptIds };
        const availableIds = new Set(prompts.map((prompt) => prompt.id));
        for (const mode of SYSTEM_PROMPT_MODES) {
          if (!availableIds.has(selectedPromptIds[mode] || '')) selectedPromptIds[mode] = null;
        }
        persistSelection(accountId, selectedPromptIds);
        set({ prompts, selectedPromptIds, isLoading: false, hasLoaded: true });
      } catch (error) {
        if (isCurrent(accountId, epoch)) set({ error: errorMessage(error), isLoading: false });
      }
    },

    selectPrompt: (mode, id) => {
      const accountId = syncAccount();
      if (!accountId || (id !== null && !get().prompts.some((prompt) => prompt.id === id))) return;
      const selectedPromptIds = { ...get().selectedPromptIds, [mode]: id };
      persistSelection(accountId, selectedPromptIds);
      set({ selectedPromptIds });
    },

    savePrompt: async (input, id) => {
      const accountId = syncAccount();
      if (!accountId || get().isSaving || get().isLoading) return null;
      const trimmed = { name: input.name.trim(), content: input.content.trim() };
      if (!trimmed.name || !trimmed.content || trimmed.name.length > 100 || trimmed.content.length > 12000) {
        set({ error: '이름은 1~100자, 내용은 1~12,000자로 입력해 주세요.' });
        return null;
      }
      const epoch = sessionEpoch;
      set({ isSaving: true, error: null });
      try {
        const prompt = id
          ? await updateSystemPrompt(id, trimmed)
          : await createSystemPrompt(trimmed);
        if (!isCurrent(accountId, epoch)) return null;
        set((state) => ({
          prompts: [prompt, ...state.prompts.filter((item) => item.id !== prompt.id)],
          isSaving: false,
        }));
        return prompt;
      } catch (error) {
        if (isCurrent(accountId, epoch)) set({ error: errorMessage(error), isSaving: false });
        return null;
      }
    },

    deletePrompt: async (id) => {
      const accountId = syncAccount();
      if (!accountId || get().isSaving || get().isLoading) return false;
      const epoch = sessionEpoch;
      set({ isSaving: true, error: null });
      try {
        await deleteSystemPrompt(id);
        if (!isCurrent(accountId, epoch)) return false;
        const selectedPromptIds = { ...get().selectedPromptIds };
        for (const mode of SYSTEM_PROMPT_MODES) {
          if (selectedPromptIds[mode] === id) selectedPromptIds[mode] = null;
        }
        persistSelection(accountId, selectedPromptIds);
        set((state) => ({
          prompts: state.prompts.filter((prompt) => prompt.id !== id),
          selectedPromptIds,
          isSaving: false,
        }));
        return true;
      } catch (error) {
        if (isCurrent(accountId, epoch)) set({ error: errorMessage(error), isSaving: false });
        return false;
      }
    },

    clearError: () => set({ error: null }),
    reset: () => {
      sessionEpoch += 1;
      // Logout clears in-memory data; account-scoped selections survive sign-in.
      set(initialState(null));
    },
  };
});

/** Resolve at send time so account switches cannot reuse another user's prompt. */
export function getSelectedSystemPromptId(mode: string): string | null {
  if (!isSystemPromptMode(mode)) return null;
  const accountId = getActiveAccountId();
  if (!accountId) return null;
  const state = useSystemPromptStore.getState();
  return state.accountId === accountId
    ? state.selectedPromptIds[mode]
    : readSelection(accountId)[mode];
}
